import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { apiCall, WEEKDAYS_SHORT } from "../calendar/calendarUtils";
import { WindowsEditor } from "./WindowsEditor";
import { DateField } from "../calendar/DateField";

export const KINDS = { camp: "Camp", urlaub: "Urlaub", sonderzeit: "Sonderzeit", sperre: "Sperre" };
const MODES = { regular: "Reguläre Zeiten", window: "Nur in diesen Zeitfenstern", blocked: "Gesperrt" };
const CATS = [["einzel", "Einzeltraining"], ["gruppe", "Gruppentraining"]];

const empty = { name: "", kind: "camp", date_from: "", date_to: "", weekdays: [], note: "",
  einzel: { mode: "window", windows: [{ start: "16:00", end: "20:00" }] }, gruppe: { mode: "regular", windows: [] } };

export function PeriodDialog({ token, period, onClose, onSaved }) {
  const [f, setF] = useState(period?.id ? period : empty);
  const [busy, setBusy] = useState(false);
  const setCat = (c, patch) => setF({ ...f, [c]: { ...f[c], ...patch } });
  const toggleDay = (d) => setF({ ...f, weekdays: f.weekdays.includes(d) ? f.weekdays.filter((x) => x !== d) : [...f.weekdays, d].sort() });
  const applyKind = (kind) => setF({ ...f, kind, ...(kind === "urlaub" || kind === "sperre" ? { einzel: { mode: "blocked", windows: [] }, gruppe: { mode: "blocked", windows: [] } } : {}) });

  const save = async () => {
    setBusy(true);
    const body = { name: f.name, kind: f.kind, date_from: f.date_from, date_to: f.date_to, weekdays: f.weekdays, note: f.note || "", einzel: f.einzel, gruppe: f.gruppe };
    try {
      await apiCall(token, period?.id ? `/booking-rules/admin/periods/${period.id}` : "/booking-rules/admin/periods", { method: period?.id ? "PUT" : "POST", body });
      toast.success("Zeitraum gespeichert");
      onSaved();
    } catch (err) { toast.error(err.message); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121212] border-white/10 text-white max-w-xl max-h-[90vh] overflow-y-auto" data-testid="period-dialog">
        <DialogHeader><DialogTitle className="font-['Bebas_Neue'] text-2xl">{period?.id ? "Zeitraum bearbeiten" : "Neuer Sonderzeitraum"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2"><Label className="text-xs text-[#A1A1AA]">Bezeichnung</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="z. B. Fußballcamp" className="bg-[#0A0A0A] border-white/20 text-white" data-testid="period-name" /></div>
            <div><Label className="text-xs text-[#A1A1AA]">Art</Label>
              <select value={f.kind} onChange={(e) => applyKind(e.target.value)} className="w-full h-10 px-2 bg-[#0A0A0A] border border-white/20 text-white rounded-md" data-testid="period-kind">
                {Object.entries(KINDS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-[#A1A1AA]">Von</Label><DateField value={f.date_from} onChange={(v) => setF({ ...f, date_from: v, date_to: f.date_to && f.date_to >= v ? f.date_to : v })} testId="period-from" /></div>
            <div><Label className="text-xs text-[#A1A1AA]">Bis einschließlich</Label><DateField value={f.date_to} onChange={(v) => setF({ ...f, date_to: v })} testId="period-to" /></div>
          </div>
          <div>
            <Label className="text-xs text-[#A1A1AA]">Nur an diesen Wochentagen (leer = alle Tage im Zeitraum)</Label>
            <div className="flex gap-1 mt-1">
              {WEEKDAYS_SHORT.map((d, i) => (
                <button key={d} type="button" onClick={() => toggleDay(i)} className={`w-9 h-9 text-xs border ${f.weekdays.includes(i) ? "bg-[#c1ff72] text-black border-[#c1ff72]" : "border-white/20 text-white"}`} data-testid={`period-wd-${i}`}>{d}</button>
              ))}
            </div>
          </div>
          {CATS.map(([c, label]) => (
            <div key={c} className="border border-white/10 p-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold">{label}</span>
                <select value={f[c].mode} onChange={(e) => setCat(c, { mode: e.target.value })} className="h-9 px-2 bg-[#0A0A0A] border border-white/20 text-white text-sm" data-testid={`period-${c}-mode`}>
                  {Object.entries(MODES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
              {f[c].mode === "window" && <WindowsEditor windows={f[c].windows} onChange={(w) => setCat(c, { windows: w })} testId={`period-${c}-windows`} />}
            </div>
          ))}
          <p className="text-xs text-[#71717A]">Sonderzeiträume schränken die regulären Zeiten nur ein. Die Wochenendsperre für Einzeltraining gilt auch während eines Camps weiter.</p>
          <div><Label className="text-xs text-[#A1A1AA]">Notiz</Label><Input value={f.note || ""} onChange={(e) => setF({ ...f, note: e.target.value })} className="bg-[#0A0A0A] border-white/20 text-white" data-testid="period-note" /></div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} className="border-white/20 text-white rounded-none">Abbrechen</Button>
            <Button onClick={save} disabled={busy || !f.name || !f.date_from || !f.date_to} className="bg-[#c1ff72] text-black hover:bg-[#a8e65c] rounded-none" data-testid="period-save">Speichern</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
